import { stringify } from 'query-string';
import type { GetConversationsResponse } from 'api/conversations';
import { HttpClient } from 'api/http-client';
import type { PostingPeriod } from 'api/revenue-entries/posting-periods/types';
import { PageSource } from 'components/organisms/revenue-entries/list/create-revenue-entry-dropdown';
import type {
  RevenueEntry,
  RevenueEntryResponse,
  RevenueEntryListResponse,
  GetRevenueEntriesParams,
  GetRevenueSummaryParams,
  RevenueSummary,
  DateRange,
  GetMissingDateRangesParams,
  RevenueUpdateSummaryRequest,
  RevenueUpdateSummaryResponse,
  GetRevenueUpdateSummaryParams,
  RevenueEntryAccess,
  RevenueEntryAccessFlags,
  PeriodLockVerificationRequest,
} from './types';

const BASE_URL = '/revenue-entries';

class RevenueEntriesApiClient extends HttpClient {
  /**
   * Get revenue entry by ID with full details
   */
  public getRevenueEntryById = (
    revenueEntryId: number,
  ): Promise<RevenueEntryResponse> => {
    return this.get(`${BASE_URL}/${revenueEntryId}`);
  };

  /**
   * Get list of revenue entries with filters
   */
  public getRevenueEntries = (
    params: GetRevenueEntriesParams = {},
  ): Promise<RevenueEntryListResponse> => {
    const queryString = stringify(params, { skipNull: true });
    const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL;

    return this.get(url);
  };

  /**
   * Create a new revenue entry
   */
  public createRevenueEntry = (
    data: Omit<RevenueEntry, 'revenueEntryId'> & {
      metaParams?: Record<string, string | number | boolean | undefined>;
    },
  ): Promise<RevenueEntryResponse> => {
    return this.post(BASE_URL, data);
  };

  /**
   * Update an existing revenue entry
   */
  public updateRevenueEntry = (
    revenueEntryId: number,
    data: Partial<RevenueEntry> & {
      metaParams?: Record<string, string | number | boolean | undefined>;
    },
  ): Promise<RevenueEntry> => {
    return this.put(`${BASE_URL}/${revenueEntryId}`, data);
  };

  /**
   * Delete a revenue entry (soft delete)
   */
  public deleteRevenueEntry = (
    revenueEntryId: number,
    context?: {
      pageSource?: PageSource;
      groupKey?: string;
      startPeriodId?: number;
      endPeriodId?: number;
      isAllPeriod?: boolean;
    },
  ): Promise<void> => {
    const queryString = context
      ? `?${stringify(context as Record<string, any>, { skipNull: true })}`
      : '';
    const url = `${BASE_URL}/${revenueEntryId}${queryString}`;

    return this.delete(url);
  };

  /**
   * Get revenue summary with filters
   */
  public getRevenueSummary = (
    params: GetRevenueSummaryParams = {},
  ): Promise<RevenueSummary> => {
    const queryString = stringify(params, { skipNull: true });
    const url = queryString
      ? `${BASE_URL}/summary?${queryString}`
      : `${BASE_URL}/summary`;

    return this.get(url);
  };

  /**
   * Get list of accessible project IDs for revenue entries
   */
  public getAccessibleProjects = (
    action: 'CREATE' | 'READ',
  ): Promise<number[]> => {
    return this.get(`${BASE_URL}/project-access?action=${action}`);
  };

  /**
   * Get access flags order/mapping (bit positions) for revenue entries
   */
  public getRevenueEntryAccessFlagsOrder = (): Promise<
    Record<RevenueEntryAccessFlags, number>
  > => {
    return this.get(`${BASE_URL}/access-flags`);
  };

  /**
   * Get revenue entry access for account or a specific project
   */
  public getRevenueEntryAccess = async (
    projectId?: number,
  ): Promise<RevenueEntryAccess> => {
    const query = projectId ? `?projectId=${projectId}` : '';

    return this.get(`${BASE_URL}/access${query}`);
  };

  /**
   * Get audit logs for a revenue entry
   */

  public getRevenueEntryAuditLogs = (
    revenueEntryId: number,
  ): Promise<GetConversationsResponse> => {
    return this.get(`${BASE_URL}/${revenueEntryId}/audit-logs`);
  };

  /**
   * Get missing date ranges for revenue entries
   * Returns date ranges where revenue entries can be created without overlapping
   */
  public getMissingDateRanges = (
    params: GetMissingDateRangesParams,
  ): Promise<DateRange[]> => {
    const queryString = stringify(params, { skipNull: true });
    const url = queryString
      ? `${BASE_URL}/missing-dates?${queryString}`
      : `${BASE_URL}/missing-dates`;

    return this.get(url);
  };

  /**
   * Get revenue update summary showing impact of edit/delete action
   * Returns period-wise breakdown of revenue changes
   */
  public getRevenueUpdateSummary = (
    request: RevenueUpdateSummaryRequest,
    params?: GetRevenueUpdateSummaryParams,
  ): Promise<RevenueUpdateSummaryResponse> => {
    const queryString = params ? stringify(params, { skipNull: true }) : '';
    const url = queryString
      ? `${BASE_URL}/update-summary?${queryString}`
      : `${BASE_URL}/update-summary`;

    return this.post(url, request);
  };

  /**
   * Verify whether the selected posting period can be locked.
   * Returns list of projects and their missing periods if gaps exist.
   */
  public verifyPeriodLock = (
    request: PeriodLockVerificationRequest,
  ): Promise<number[]> => {
    return this.post(`${BASE_URL}/period-lock-verification`, request);
  };

  /**
   * Get allowed posting periods for creating revenue entries for a project/budget
   */
  public getAllowedRevenuePeriods = (
    projectId: number,
    budgetId?: number,
  ): Promise<PostingPeriod[]> => {
    return this.get(`${BASE_URL}/project/${projectId}/allowed-periods`, {
      params: {
        budgetId,
      },
    });
  };
}

export const RevenueEntriesApi = new RevenueEntriesApiClient();
